/** The admin's inbox: every question the service could not answer on its own.
 *
 * The rule the whole system is built on is that nothing is dropped silently.
 * If the merge job skipped an upload, refused to auto-merge a circuit, or
 * found the repository changed underneath it, there is a row here saying
 * which question it could not answer and what it had in front of it when it
 * gave up. This screen is where a person answers.
 *
 * So the layout is a mail client, not a table: a narrow list of what is
 * waiting, and beside it everything known about the one you picked. A table
 * would show eight issues badly; this shows one properly, which is the only
 * way a decision gets made. The selection lives in the address bar, so a
 * particular issue can be linked to — the `error` webhook does exactly that.
 *
 * The resolve buttons are the part worth being careful about. They are
 * rendered from the list the service attached to the issue, never from a
 * table written here, because resolving is how the decision is *taken* — it
 * re-queues the upload, rejects it, moves a source binding, disables an
 * account — and the endpoint refuses anything its type does not offer. A
 * button spelled out in the portal would be a promise this screen cannot
 * keep. The same reasoning puts the note field on the page: several
 * resolutions are refused without one, and a button that answers
 * `note_required` is a button that does not work.
 */

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { adminIssue, adminIssues, resolveIssue, uploadBundleUrl } from '@/lib/api';
import { issueGuide } from '@/components/admin/issueGuide';
import type { IssueGuide } from '@/components/admin/issueGuide';
import { count, DASH, relativeTime } from '@/lib/format';
import type {
  Issue,
  IssueDetail,
  IssueList,
  IssueResolution,
  IssueStatus,
  Severity,
} from '@/lib/types';
import { EmptyState, ErrorNote, Kicker, LoadingPanel, Page, Spinner } from '@/components/ui';
import {
  AdminHeader,
  CandidateMap,
  KeyValueGrid,
  LineCompare,
  SeverityDot,
  detailRows,
  lineComparison,
  resolutionNote,
  setAdminPulseIssues,
  severityColour,
  showsCandidate,
} from '@/components/admin';

const STATUSES: readonly IssueStatus[] = ['open', 'resolved', 'ignored'];

const SEVERITIES: readonly Severity[] = ['error', 'warn', 'info'];

function isStatus(value: string): value is IssueStatus {
  return (STATUSES as readonly string[]).includes(value);
}

function isSeverity(value: string): value is Severity {
  return (SEVERITIES as readonly string[]).includes(value);
}

/** A resolution that throws work away reads as the one that does. The hue
 *  comes from what the service says the resolution *does*, not from a list of
 *  button labels kept in step by hand. */
function isDestructive(resolution: IssueResolution): boolean {
  return resolution.effect === 'reject_upload' || resolution.effect === 'disable_account';
}

/** Why the list is empty, which is not the same sentence for a queue that is
 *  clear as for a filter that matches nothing. */
function emptyTitle(status: IssueStatus, severity: Severity | null): string {
  if (severity) return `no ${status} ${severity} issues`;
  return status === 'open' ? 'the queue is empty' : `nothing ${status}`;
}

/** The counts above the inbox, which are also its filter. */
function CountTag({
  label,
  value,
  active,
  onClick,
}: {
  label: string;
  value: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`tag ${active ? 'tag-accent' : 'tag-neutral'} tabular-nums`}
      aria-pressed={active}
      onClick={onClick}
      style={{ border: 0, cursor: 'pointer', font: 'inherit', fontSize: 11 }}
    >
      {label} {count(value)}
    </button>
  );
}

export default function AdminIssues() {
  const params = useParams();
  const [search, setSearch] = useSearchParams();

  const rawStatus = search.get('status') ?? 'open';
  const status: IssueStatus = isStatus(rawStatus) ? rawStatus : 'open';
  const rawSeverity = search.get('severity') ?? '';
  const severity: Severity | null = isSeverity(rawSeverity) ? rawSeverity : null;
  // A route of `/admin/issues/:issueId` is honoured if one is ever added, so
  // the webhook's deep link keeps working; today the selection is a query.
  const selectedId = params['issueId'] ?? search.get('issue') ?? '';

  const [list, setList] = useState<IssueList | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<unknown>(null);

  const [detail, setDetail] = useState<IssueDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<unknown>(null);

  const [note, setNote] = useState('');
  const [noteRequired, setNoteRequired] = useState(false);
  const [applying, setApplying] = useState<string | null>(null);
  const [resolveError, setResolveError] = useState<unknown>(null);
  const [effects, setEffects] = useState<string[]>([]);
  const noteField = useRef<HTMLTextAreaElement | null>(null);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const setParam = useCallback(
    (entries: Record<string, string | null>, replace = false) => {
      setSearch(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(entries)) {
            if (value === null || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace },
      );
    },
    [setSearch],
  );

  const loadList = useCallback(async () => {
    try {
      const reply = await adminIssues({ status, severity: severity ?? undefined, limit: 100 });
      if (!alive.current) return;
      setList(reply);
      setListError(null);
      // The header's count and this one should never disagree.
      setAdminPulseIssues(reply.counts.open);
    } catch (thrown) {
      if (alive.current) setListError(thrown);
    } finally {
      if (alive.current) setListLoading(false);
    }
  }, [status, severity]);

  useEffect(() => {
    setListLoading(true);
    void loadList();
  }, [loadList]);

  // Nothing chosen and something to choose: open the newest, which is what a
  // queue is for. Replaced rather than pushed, so Back leaves the screen
  // instead of stepping through selections nobody made.
  useEffect(() => {
    if (selectedId) return;
    const first = list?.items[0];
    if (first) setParam({ issue: first.id }, true);
  }, [selectedId, list, setParam]);

  const loadDetail = useCallback(async (id: string) => {
    try {
      const reply = await adminIssue(id);
      if (!alive.current) return;
      setDetail(reply);
      setDetailError(null);
    } catch (thrown) {
      if (alive.current) {
        setDetail(null);
        setDetailError(thrown);
      }
    } finally {
      if (alive.current) setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    setNote('');
    setNoteRequired(false);
    setResolveError(null);
    setEffects([]);
    if (!selectedId) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    setDetailLoading(true);
    void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const apply = useCallback(
    async (issue: IssueDetail, resolution: IssueResolution) => {
      if (resolution.needsNote && note.trim() === '') {
        setNoteRequired(true);
        noteField.current?.focus();
        return;
      }
      setApplying(resolution.id);
      setResolveError(null);
      setNoteRequired(false);
      try {
        const applied = await resolveIssue(issue.id, {
          resolution: resolution.id,
          note: note.trim() || undefined,
        });
        if (!alive.current) return;
        setEffects(applied.effects);
        setNote('');
        // Both halves move: the row leaves the open queue, and the card
        // becomes a record of what was decided rather than a set of choices.
        await Promise.all([loadList(), loadDetail(issue.id)]);
      } catch (thrown) {
        if (alive.current) setResolveError(thrown);
      } finally {
        if (alive.current) setApplying(null);
      }
    },
    [note, loadList, loadDetail],
  );

  const counts = list?.counts;

  return (
    <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <AdminHeader openIssues={counts?.open} />

      <section
        aria-label="Issue queue"
        className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(300px,380px)_minmax(0,1fr)]"
        style={{ alignItems: 'start' }}
      >
        {/* ── the inbox ─────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              gap: 6,
              paddingBottom: 10,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <CountTag
              label="open"
              value={counts?.open ?? 0}
              active={status === 'open'}
              onClick={() => setParam({ status: 'open', issue: null })}
            />
            <CountTag
              label="resolved"
              value={counts?.resolved ?? 0}
              active={status === 'resolved'}
              onClick={() => setParam({ status: 'resolved', issue: null })}
            />
            <CountTag
              label="ignored"
              value={counts?.ignored ?? 0}
              active={status === 'ignored'}
              onClick={() => setParam({ status: 'ignored', issue: null })}
            />
            {severity ? (
              <button
                type="button"
                className="tag tag-outline"
                onClick={() => setParam({ severity: null, issue: null })}
                title="show every severity"
                style={{ cursor: 'pointer', font: 'inherit', fontSize: 11 }}
              >
                {severity} only ✕
              </button>
            ) : null}
          </div>

          {listError ? (
            <ErrorNote
              error={listError}
              title="the queue could not be read"
              onRetry={() => {
                setListLoading(true);
                void loadList();
              }}
            />
          ) : null}

          {listLoading && !list ? (
            <LoadingPanel label="loading the queue" />
          ) : list && list.items.length === 0 ? (
            <EmptyState compact title={emptyTitle(status, severity)}>
              {status === 'open'
                ? 'Every question the job asked has been answered.'
                : 'Issues land here once a person has closed them.'}
            </EmptyState>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {(list?.items ?? []).map((issue: Issue) => {
                const chosen = issue.id === selectedId;
                return (
                  <li key={issue.id}>
                    <button
                      type="button"
                      aria-current={chosen ? 'true' : undefined}
                      onClick={() => setParam({ issue: issue.id })}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '8px minmax(0,1fr) auto',
                        gap: 12,
                        alignItems: 'start',
                        width: '100%',
                        textAlign: 'left',
                        font: 'inherit',
                        color: 'inherit',
                        cursor: 'pointer',
                        border: 0,
                        padding: '10px 12px',
                        borderRadius: 'var(--radius-md)',
                        background: chosen ? 'var(--color-surface)' : 'transparent',
                      }}
                    >
                      <SeverityDot severity={issue.severity} style={{ marginTop: 5 }} />
                      <span
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                          minWidth: 0,
                        }}
                      >
                        <code style={{ fontSize: 12 }} title={issue.label}>
                          {issue.type}
                        </code>
                        <span style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>
                          {issue.official_id || DASH}
                        </span>
                        <span
                          style={{
                            fontSize: 11.5,
                            color: 'var(--color-neutral-600)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {issue.summary}
                        </span>
                      </span>
                      <span
                        className="tabular-nums"
                        style={{
                          fontSize: 11,
                          color: 'var(--color-neutral-600)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {relativeTime(issue.created_at)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* ── the one you picked ────────────────────────────────────── */}
        <div
          style={{
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-sm)',
            padding: '18px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minWidth: 0,
          }}
        >
          {detailLoading && !detail ? (
            <div style={{ padding: '32px 0', display: 'grid', placeItems: 'center' }}>
              <Spinner size={20} label="loading the issue" />
            </div>
          ) : detailError ? (
            <ErrorNote
              error={detailError}
              title="that issue could not be read"
              onRetry={() => {
                if (!selectedId) return;
                setDetailLoading(true);
                void loadDetail(selectedId);
              }}
            />
          ) : !detail ? (
            <EmptyState title="nothing selected">
              Pick an issue from the queue to see what the job had in front of it.
            </EmptyState>
          ) : (
            <IssueCard
              issue={detail}
              note={note}
              noteRequired={noteRequired}
              noteField={noteField}
              onNote={(value) => {
                setNote(value);
                if (value.trim()) setNoteRequired(false);
              }}
              applying={applying}
              effects={effects}
              error={resolveError}
              onApply={(resolution) => void apply(detail, resolution)}
            />
          )}
        </div>
      </section>
    </Page>
  );
}

/** What happened and what to do, in words, above the job's raw numbers. */
function GuideBox({ guide }: { guide: IssueGuide }) {
  return (
    <section
      aria-label="What happened and what to do"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 14,
        padding: '12px 14px',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-neutral-800)',
        background: 'color-mix(in srgb, var(--color-neutral-900) 60%, transparent)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
        <Kicker tone="muted">what happened</Kicker>
        {guide.happened.map((line) => (
          <p
            key={line}
            style={{ margin: 0, fontSize: 13, color: 'var(--color-neutral-200)', textWrap: 'pretty' }}
          >
            {line}
          </p>
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
        <Kicker tone="muted">what to do</Kicker>
        <ol
          style={{
            margin: 0,
            paddingLeft: 18,
            fontSize: 13,
            color: 'var(--color-neutral-200)',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {guide.todo.map((line) => (
            <li key={line} style={{ textWrap: 'pretty' }}>
              {line}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/** The card itself, split out so the screen above reads as loading, failure,
 *  emptiness and content rather than as one long conditional. */
function IssueCard({
  issue,
  note,
  noteRequired,
  noteField,
  onNote,
  applying,
  effects,
  error,
  onApply,
}: {
  issue: IssueDetail;
  note: string;
  noteRequired: boolean;
  noteField: MutableRefObject<HTMLTextAreaElement | null>;
  onNote: (value: string) => void;
  applying: string | null;
  effects: string[];
  error: unknown;
  onApply: (resolution: IssueResolution) => void;
}) {
  const rows = detailRows(issue.detail);
  const guide = issueGuide(issue);
  const comparison = lineComparison(issue);
  const closingNote = resolutionNote(issue.detail);
  const open = issue.status === 'open';

  const track = issue.upload?.track_name || issue.official_id;
  const who = issue.user?.display_name ?? issue.user_id;
  const subject = [track, issue.upload_id ? `upload ${issue.upload_id}` : null, who].filter(
    (part): part is string => Boolean(part),
  );

  const settled = issue.resolutions.find((item) => item.id === issue.resolution);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <SeverityDot severity={issue.severity} />
            <span
              style={{
                fontSize: 11,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--color-neutral-500)',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ color: severityColour(issue.severity) }}>{issue.severity}</span> · raised
              by {issue.raised_by} · {relativeTime(issue.created_at)}
            </span>
          </div>
          <h2
            title={issue.label}
            style={{
              fontSize: 22,
              margin: '6px 0 0',
              fontFamily: 'var(--font-mono)',
              fontWeight: 500,
              overflowWrap: 'anywhere',
            }}
          >
            {issue.type}
          </h2>
          <div
            style={{ fontSize: 13.5, color: 'var(--color-neutral-400)', marginTop: 2 }}
          >
            {subject.length > 0
              ? subject.map((part, index) => (
                  <span key={part}>
                    {index > 0 ? ' · ' : ''}
                    <span
                      style={
                        part.startsWith('upload ') ? { fontFamily: 'var(--font-mono)' } : undefined
                      }
                    >
                      {part}
                    </span>
                  </span>
                ))
              : 'no circuit, no upload — this is about an account'}
          </div>
        </div>

        {issue.upload_id ? (
          <a
            className="btn btn-secondary"
            style={{ fontSize: 12 }}
            href={uploadBundleUrl(issue.upload_id)}
            target="_blank"
            rel="noreferrer noopener"
          >
            Open raw bundle
          </a>
        ) : null}
      </div>

      <p
        style={{
          margin: 0,
          fontSize: 14,
          textWrap: 'pretty',
          color: 'var(--color-neutral-200)',
        }}
      >
        {issue.summary}
      </p>

      {open ? <GuideBox guide={guide} /> : null}

      <KeyValueGrid items={rows} label="what the job measured" />

      {comparison ? (
        <LineCompare contributor={comparison.contributor} signature={comparison.signature} />
      ) : null}

      {/* The numbers above say the gate declined; this is the thing it
          declined, so the decision can be made by looking. */}
      {showsCandidate(issue) ? (
        <CandidateMap officialId={issue.official_id} refreshKey={issue.id} />
      ) : null}

      {/* ── what may be done about it ─────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 'auto' }}>
        {open ? (
          <>
            <Kicker tone="muted">resolve — applied immediately, written to audit</Kicker>

            <div className="field">
              <label htmlFor="issue-note">
                note{' '}
                <span style={{ color: 'var(--color-neutral-600)' }}>
                  — kept with the issue; some resolutions need one
                </span>
              </label>
              <textarea
                id="issue-note"
                ref={noteField}
                className="input"
                rows={2}
                value={note}
                aria-invalid={noteRequired || undefined}
                aria-describedby={noteRequired ? 'issue-note-required' : undefined}
                style={{
                  minHeight: 56,
                  fontSize: 13,
                  borderColor: noteRequired ? 'var(--color-error)' : undefined,
                }}
                placeholder="what you decided, and why"
                onChange={(event) => onNote(event.target.value)}
              />
              {noteRequired ? (
                <p
                  id="issue-note-required"
                  style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--color-error)' }}
                >
                  that resolution needs a note — the contributor is told what it says.
                </p>
              ) : null}
            </div>

            {error ? <ErrorNote error={error} title="the resolution was not applied" compact /> : null}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {issue.resolutions
                .filter((resolution) => resolution.status === 'resolved')
                .map((resolution) => (
                  <button
                    key={resolution.id}
                    type="button"
                    className={`btn ${isDestructive(resolution) ? 'btn-danger' : 'btn-primary'}`}
                    style={{
                      fontSize: 12.5,
                      outline:
                        guide.recommended === resolution.id
                          ? '2px solid var(--color-accent-400)'
                          : undefined,
                      outlineOffset: 2,
                    }}
                    title={resolution.description}
                    disabled={applying !== null}
                    onClick={() => onApply(resolution)}
                  >
                    {applying === resolution.id ? 'Applying…' : resolution.label}
                  </button>
                ))}

              {issue.resolutions
                .filter((resolution) => resolution.status === 'ignored')
                .map((resolution) => (
                  <button
                    key={resolution.id}
                    type="button"
                    className="btn btn-ghost"
                    style={{
                      fontSize: 12.5,
                      color: 'var(--color-neutral-500)',
                      marginLeft: 'auto',
                    }}
                    title={resolution.description}
                    disabled={applying !== null}
                    onClick={() => onApply(resolution)}
                  >
                    {applying === resolution.id ? 'Applying…' : 'Ignore until next trigger'}
                  </button>
                ))}

              {issue.resolutions.length === 0 ? (
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--color-neutral-500)' }}>
                  This build has no rules for <code>{issue.type}</code> issues, so there is nothing
                  it can offer to do about one.
                </p>
              ) : null}
            </div>

            {issue.resolutions.length > 0 ? (
              <dl
                style={{
                  margin: 0,
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, max-content) minmax(0, 1fr)',
                  gap: '3px 12px',
                  fontSize: 12,
                  color: 'var(--color-neutral-500)',
                }}
              >
                {issue.resolutions.map((resolution) => (
                  <Fragment key={resolution.id}>
                    <dt style={{ color: 'var(--color-neutral-300)' }}>
                      {resolution.status === 'ignored' ? 'Ignore until next trigger' : resolution.label}
                      {guide.recommended === resolution.id ? (
                        <span style={{ color: 'var(--color-accent-400)' }}> · suggested</span>
                      ) : null}
                    </dt>
                    <dd style={{ margin: 0, textWrap: 'pretty' }}>
                      {resolution.description}
                      {resolution.needsNote ? ' (needs a note)' : ''}
                    </dd>
                  </Fragment>
                ))}
              </dl>
            ) : null}
          </>
        ) : (
          <>
            <Kicker tone="muted">{issue.status}</Kicker>
            <p style={{ margin: 0, fontSize: 13.5, color: 'var(--color-neutral-300)' }}>
              {settled?.label || issue.resolution || DASH}
              {issue.resolved_by_name ? ` · by ${issue.resolved_by_name}` : ''}
              {issue.resolved_at ? ` · ${relativeTime(issue.resolved_at)}` : ''}
            </p>
            {settled ? (
              <p style={{ margin: 0, fontSize: 12, color: 'var(--color-neutral-500)' }}>
                {settled.description}
              </p>
            ) : null}
            {closingNote ? (
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  color: 'var(--color-neutral-400)',
                  textWrap: 'pretty',
                }}
              >
                “{closingNote}”
              </p>
            ) : null}
            {effects.length > 0 ? (
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 16,
                  fontSize: 12,
                  color: 'var(--color-neutral-500)',
                }}
              >
                {effects.map((effect) => (
                  <li key={effect}>{effect}</li>
                ))}
              </ul>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}
